import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

const fieldSchema = z.object({
  label: z.string().min(1).max(60),
  field_type: z.enum(["text", "number", "date", "dropdown", "link"]),
  dropdown_options: z.array(z.string().min(1).max(60)).max(20).nullable().optional(),
});

const createBoardSchema = z.object({
  name: z.string().min(1).max(80),
  board_type: z.enum(["department", "personal"]),
  department: z.string().nullable().optional(),
  columns: z.array(z.string().min(1).max(40)).min(3).max(6),
  fields: z.array(fieldSchema).default([]),
});

export const createKanbanBoard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => createBoardSchema.parse(d))
  .handler(async ({ data, context }) => {
    const sb = context.supabase as never as {
      from: (t: string) => {
        insert: (v: unknown) => { select: () => { single: () => Promise<{ data: { id: string } | null; error: { message: string } | null }> } };
      };
    };
    const supa = context.supabase;
    const { data: board, error } = await supa
      .from("kanban_boards")
      .insert({
        name: data.name,
        board_type: data.board_type,
        department: data.board_type === "department" ? (data.department as never) : null,
        owner_id: context.userId,
      } as never)
      .select()
      .single();
    if (error || !board) throw new Error(error?.message ?? "Failed to create board");
    const boardId = (board as { id: string }).id;

    const cols = data.columns.map((name, i) => ({ board_id: boardId, name, position: i }));
    const { error: cErr } = await supa.from("kanban_columns").insert(cols as never);
    if (cErr) throw new Error(cErr.message);

    if (data.fields.length > 0) {
      const fields = data.fields.map((f, i) => ({
        board_id: boardId,
        label: f.label,
        field_type: f.field_type,
        dropdown_options: f.field_type === "dropdown" ? (f.dropdown_options ?? []) : null,
        position: i,
      }));
      const { error: fErr } = await supa.from("kanban_field_definitions").insert(fields as never);
      if (fErr) throw new Error(fErr.message);
    }
    void sb;
    return { id: boardId };
  });

const approveSchema = z.object({
  board_id: z.string().uuid(),
  action: z.enum(["approve", "reject", "archive"]),
});

export const reviewBoard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => approveSchema.parse(d))
  .handler(async ({ data, context }) => {
    const supa = context.supabase;
    const { data: roleRows } = await supa
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const isAdmin = (roleRows ?? []).some((r: { role: string }) => r.role === "admin");
    if (!isAdmin && data.action !== "archive") throw new Error("Admin only");

    if (data.action === "approve") {
      const { error } = await supa
        .from("kanban_boards")
        .update({ status: "active" } as never)
        .eq("id", data.board_id);
      if (error) throw new Error(error.message);
    } else if (data.action === "reject") {
      const { error } = await supa.from("kanban_boards").delete().eq("id", data.board_id);
      if (error) throw new Error(error.message);
    } else {
      const { error } = await supa
        .from("kanban_boards")
        .update({ status: "archived" } as never)
        .eq("id", data.board_id);
      if (error) throw new Error(error.message);
    }
    return { ok: true };
  });

const setKanbanPermsSchema = z.object({
  user_id: z.string().uuid(),
  is_team_lead: z.boolean(),
});

export const setKanbanPermissions = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => setKanbanPermsSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: roleRows } = await supabaseAdmin
      .from("user_roles")
      .select("role")
      .eq("user_id", context.userId);
    const isAdmin = (roleRows ?? []).some((r) => r.role === "admin");
    if (!isAdmin) throw new Error("Admin only");
    const { error } = await supabaseAdmin
      .from("profiles")
      .update({ is_team_lead: data.is_team_lead } as never)
      .eq("id", data.user_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });


const createCardsSchema = z.object({
  board_id: z.string().uuid(),
  column_id: z.string().uuid(),
  title: z.string().min(1).max(200),
  assignee_ids: z.array(z.string().uuid()).max(50).default([]),
  work_date: z.string().nullable().optional(),
  priority: z.enum(["P1", "P2", "P3"]),
  total_count: z.number().int().positive().nullable().optional(),
  note: z.string().max(4000).nullable().optional(),
  field_values: z.record(z.string().uuid(), z.string()).optional(),
  as_draft: z.boolean().optional(),
  draft_count: z.number().int().min(1).max(20).optional(),
});

export const createKanbanCards = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => createCardsSchema.parse(d))
  .handler(async ({ data, context }) => {
    const supa = context.supabase as never as {
      from: (t: string) => {
        select: (s: string) => { eq: (k: string, v: string) => Promise<{ data: unknown[] | null; error: { message: string } | null }> };
        insert: (rows: unknown) => { select: (s?: string) => Promise<{ data: { id: string }[] | null; error: { message: string } | null }> };
        delete: () => { in: (k: string, v: string[]) => Promise<{ error: { message: string } | null }> };
      };
    };

    const asDraft = data.as_draft === true;
    const draftCount = asDraft ? Math.max(1, data.draft_count ?? 1) : 0;

    if (!asDraft) {
      if (data.assignee_ids.length === 0) throw new Error("At least one assignee required");
      const { data: members, error: mErr } = await supa
        .from("kanban_board_members")
        .select("user_id")
        .eq("board_id", data.board_id);
      if (mErr) throw new Error(mErr.message);
      const memberIds = new Set(((members ?? []) as { user_id: string }[]).map((m) => m.user_id));
      for (const aid of data.assignee_ids) {
        if (!memberIds.has(aid)) throw new Error("One or more assignees are not members of this board");
      }
    }


    const { data: existing } = await supa
      .from("kanban_cards")
      .select("position")
      .eq("column_id", data.column_id);
    const maxPos = ((existing ?? []) as { position: number }[]).reduce(
      (m, r) => (r.position > m ? r.position : m),
      0,
    );

    const baseRow = {
      board_id: data.board_id,
      column_id: data.column_id,
      title: data.title,
      priority: data.priority,
      total_count: data.total_count ?? null,
      note: data.note ?? null,
      created_by: context.userId,
    };

    const rows = asDraft
      ? Array.from({ length: draftCount }, (_, i) => ({
          ...baseRow,
          assignee_id: null,
          work_date: null,
          planning_state: "unplaced",
          position: maxPos + (i + 1) * 1000,
        }))
      : data.assignee_ids.map((aid, i) => ({
          ...baseRow,
          assignee_id: aid,
          work_date: data.work_date ?? null,
          planning_state: "placed",
          position: maxPos + (i + 1) * 1000,
        }));


    const { data: inserted, error: insErr } = await supa
      .from("kanban_cards")
      .insert(rows)
      .select("id");
    if (insErr || !inserted) throw new Error(insErr?.message ?? "Failed to create cards");
    const insertedIds = (inserted as { id: string }[]).map((r) => r.id);

    const fvEntries = Object.entries(data.field_values ?? {}).filter(
      ([, v]) => v && v.trim() !== "",
    );
    if (fvEntries.length > 0) {
      const fvRows = insertedIds.flatMap((cid) =>
        fvEntries.map(([field_id, value]) => ({ card_id: cid, field_id, value })),
      );
      const { error: fvErr } = await supa
        .from("kanban_field_values")
        .insert(fvRows)
        .select();
      if (fvErr) {
        await supa.from("kanban_cards").delete().in("id", insertedIds);
        throw new Error(`Field values failed: ${fvErr.message}`);
      }
    }

    return { ids: insertedIds, count: insertedIds.length };
  });

const placeSchema = z.object({
  card_id: z.string().uuid(),
  assignee_id: z.string().uuid(),
  work_date: z.string(),
});

export const placeKanbanCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => placeSchema.parse(d))
  .handler(async ({ data, context }) => {
    const supa = context.supabase;
    const { error } = await supa
      .from("kanban_cards")
      .update({
        assignee_id: data.assignee_id,
        work_date: data.work_date,
        planning_state: "placed",
      } as never)
      .eq("id", data.card_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const unplaceSchema = z.object({ card_id: z.string().uuid() });

export const unplaceKanbanCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => unplaceSchema.parse(d))
  .handler(async ({ data, context }) => {
    const supa = context.supabase;
    // must clear assignee+work_date before flipping state due to trigger checking NEW row
    const { error } = await supa
      .from("kanban_cards")
      .update({
        assignee_id: null,
        work_date: null,
        planning_state: "unplaced",
      } as never)
      .eq("id", data.card_id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

const distributeSchema = z.object({
  card_id: z.string().uuid(),
  assignee_ids: z.array(z.string().uuid()).min(1).max(50),
  work_date: z.string(),
});

/**
 * Take a draft (or placed) card, place it for the first assignee,
 * and duplicate it (with custom-field values) for the rest.
 * Result: N independent placed cards, one per assignee.
 */
export const distributeKanbanCard = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => distributeSchema.parse(d))
  .handler(async ({ data, context }) => {
    const supa = context.supabase as never as {
      from: (t: string) => {
        select: (s: string) => {
          eq: (k: string, v: string) => {
            maybeSingle: () => Promise<{ data: unknown; error: { message: string } | null }>;
          } & Promise<{ data: unknown[] | null; error: { message: string } | null }>;
        };
        insert: (rows: unknown) => { select: (s?: string) => Promise<{ data: { id: string }[] | null; error: { message: string } | null }> };
        update: (v: unknown) => { eq: (k: string, val: string) => Promise<{ error: { message: string } | null }> };
        delete: () => { in: (k: string, v: string[]) => Promise<{ error: { message: string } | null }> };
      };
    };

    const { data: original, error: oErr } = await supa
      .from("kanban_cards")
      .select("*")
      .eq("id", data.card_id)
      .maybeSingle();
    if (oErr) throw new Error(oErr.message);
    const card = original as null | {
      id: string; board_id: string; column_id: string; title: string;
      note: string | null; priority: string; total_count: number | null;
      position: number;
    };
    if (!card) throw new Error("Card not found");

    // verify members
    const { data: members, error: mErr } = await supa
      .from("kanban_board_members")
      .select("user_id")
      .eq("board_id", card.board_id);
    if (mErr) throw new Error(mErr.message);
    const memberIds = new Set(((members ?? []) as { user_id: string }[]).map((m) => m.user_id));
    for (const aid of data.assignee_ids) {
      if (!memberIds.has(aid)) throw new Error("One or more assignees are not members of this board");
    }

    // copy field values
    const { data: fvs } = await supa
      .from("kanban_field_values")
      .select("field_id, value")
      .eq("card_id", card.id);
    const fvList = (fvs ?? []) as { field_id: string; value: string | null }[];

    // place the original for the first assignee
    const [firstAssignee, ...rest] = data.assignee_ids;
    const { error: upErr } = await supa
      .from("kanban_cards")
      .update({
        assignee_id: firstAssignee,
        work_date: data.work_date,
        planning_state: "placed",
      } as never)
      .eq("id", card.id);
    if (upErr) throw new Error(upErr.message);

    let newIds: string[] = [];
    if (rest.length > 0) {
      const rows = rest.map((aid, i) => ({
        board_id: card.board_id,
        column_id: card.column_id,
        title: card.title,
        note: card.note,
        priority: card.priority,
        total_count: card.total_count,
        assignee_id: aid,
        work_date: data.work_date,
        planning_state: "placed",
        position: card.position + (i + 1) * 100,
        created_by: context.userId,
      }));
      const { data: ins, error: insErr } = await supa
        .from("kanban_cards")
        .insert(rows)
        .select("id");
      if (insErr || !ins) throw new Error(insErr?.message ?? "Failed to duplicate");
      newIds = (ins as { id: string }[]).map((r) => r.id);

      if (fvList.length > 0) {
        const fvRows = newIds.flatMap((cid) =>
          fvList
            .filter((f) => f.value != null && f.value !== "")
            .map((f) => ({ card_id: cid, field_id: f.field_id, value: f.value })),
        );
        if (fvRows.length > 0) {
          const { error: fvErr } = await supa
            .from("kanban_field_values")
            .insert(fvRows)
            .select();
          if (fvErr) {
            await supa.from("kanban_cards").delete().in("id", newIds);
            throw new Error(`Field values failed: ${fvErr.message}`);
          }
        }
      }
    }

    return { ids: [card.id, ...newIds], count: 1 + newIds.length };
  });
